import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import axios from '../utils/axiosInstance';
import SignatureCanvas from "react-signature-canvas";
import "bootstrap-icons/font/bootstrap-icons.css";

const API = "/umutekano";

const Styles = () => (
  <style>{`
@import url('https://fonts.googleapis.com/css2?family=Nunito+Sans:wght@300;400;500;600;700;800;900&family=Noto+Serif:wght@400;700&display=swap');

/* ── A4 Paper Container ── */
.uic-paper {
  width: 100%;
  max-width: 900px;
  background: #ffffff;
  border-radius: 6px;
  box-shadow: 0 4px 24px rgba(0,0,0,0.10), 0 1px 4px rgba(0,0,0,0.06);
  padding: 50px 55px 40px;
  margin: 0 auto;
  position: relative;
  font-family: 'Nunito Sans', system-ui, sans-serif;
  color: #1e293b;
}
.uic-paper * { box-sizing: border-box; }

/* ── Document Header ── */
.uic-header {
  text-align: center;
  padding-bottom: 28px;
  border-bottom: 3px solid #1a3a5c;
  margin-bottom: 28px;
  position: relative;
}
.uic-header::after {
  content: '';
  position: absolute;
  bottom: -6px;
  left: 50%;
  transform: translateX(-50%);
  width: 80px;
  height: 3px;
  background: #d4a843;
  border-radius: 2px;
}
.uic-header-republic {
  font-family: 'Noto Serif', serif;
  font-size: 18px;
  font-weight: 700;
  color: #1a3a5c;
  letter-spacing: 2.5px;
  text-transform: uppercase;
  margin: 0 0 4px;
}
.uic-header-title {
  font-family: 'Noto Serif', serif;
  font-size: 22px;
  font-weight: 700;
  color: #b8860b;
  text-transform: uppercase;
  letter-spacing: 1.5px;
  margin: 0 0 12px;
}
.uic-header-docnum {
  display: inline-block;
  font-size: 13px;
  font-weight: 600;
  color: #ffffff;
  background: #1a3a5c;
  padding: 6px 18px;
  border-radius: 20px;
  letter-spacing: 0.5px;
}
.uic-header-motto {
  font-size: 11px;
  color: #64748b;
  margin-top: 6px;
  font-style: italic;
}

/* ── Section ── */
.uic-section {
  margin-bottom: 24px;
}
.uic-section-title {
  font-size: 13px;
  font-weight: 700;
  color: #1a3a5c;
  text-transform: uppercase;
  letter-spacing: 1px;
  padding: 8px 14px;
  background: #f1f5f9;
  border-left: 4px solid #1a3a5c;
  margin: 0 0 14px;
  display: flex;
  align-items: center;
  gap: 8px;
}
.uic-section-title i { font-size: 14px; color: #d4a843; }
.uic-section-body { padding: 0 4px; }

/* ── Row Layout ── */
.uic-row {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px 20px;
  margin-bottom: 4px;
}
.uic-row-full { grid-template-columns: 1fr; }

/* ── Fields ── */
.uic-field { margin-bottom: 2px; }
.uic-field label {
  display: block;
  font-size: 11px;
  font-weight: 600;
  color: #475569;
  margin-bottom: 4px;
  letter-spacing: 0.3px;
}
.uic-field label .required { color: #dc2626; }
.uic-field input,
.uic-field textarea {
  width: 100%;
  padding: 10px 12px;
  border: 1px solid #d1d9e6;
  border-radius: 5px;
  font-size: 14px;
  color: #1e293b;
  background: #f8fafc;
  transition: all 0.2s ease;
  font-family: inherit;
}
.uic-field input:focus,
.uic-field textarea:focus {
  outline: none;
  border-color: #1a3a5c;
  box-shadow: 0 0 0 3px rgba(26,58,92,0.10);
  background: #ffffff;
}
.uic-field textarea {
  min-height: 100px;
  resize: vertical;
  line-height: 1.6;
}
.uic-description-area { min-height: 250px !important; }

/* ── Signature Grid ── */
.uic-signatures {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 14px;
  margin-top: 8px;
}
.uic-signature-slot {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 14px;
  text-align: center;
  background: #fafbfc;
}
.uic-sig-label {
  font-size: 10px;
  font-weight: 700;
  color: #64748b;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 4px;
}
.uic-sig-name {
  font-size: 13px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
  min-height: 20px;
}
.uic-sig-placeholder {
  font-size: 11px;
  color: #94a3b8;
  font-style: italic;
  min-height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px dashed #d1d9e6;
  border-radius: 4px;
  padding: 8px;
  background: #fff;
}
.uic-sig-image {
  max-height: 50px;
  display: block;
  margin: 0 auto;
  border-radius: 3px;
  border: 1px solid #e2e8f0;
  padding: 4px;
  background: #fff;
}
.uic-sig-status {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 10px;
  font-weight: 600;
  padding: 2px 8px;
  border-radius: 10px;
  margin-top: 6px;
}
.uic-sig-status.signed { background: #dcfce7; color: #16a34a; }
.uic-sig-status.pending { background: #fef3c7; color: #b45309; }

/* ── Signature Canvas ── */
.uic-sig-pad {
  background: #f8fafc;
  border: 2px dashed #cbd5e1;
  border-radius: 8px;
  padding: 14px;
  margin-top: 8px;
}
.uic-sig-pad canvas {
  width: 100% !important;
  max-width: 500px;
  height: 150px !important;
  display: block;
  margin: 0 auto;
  cursor: crosshair;
  touch-action: none;
  border-radius: 5px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
}
.uic-sig-actions {
  display: flex;
  gap: 8px;
  justify-content: center;
  margin-top: 8px;
  flex-wrap: wrap;
}

/* ── Buttons ── */
.uic-actions {
  display: flex;
  gap: 10px;
  margin-top: 28px;
  padding-top: 24px;
  border-top: 2px solid #e2e8f0;
  flex-wrap: wrap;
}
.uic-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  padding: 10px 20px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  font-family: inherit;
  min-height: 42px;
}
.uic-btn:disabled { opacity: 0.5; cursor: not-allowed; }
.uic-btn-primary { background: #1a3a5c; color: #fff; }
.uic-btn-primary:hover:not(:disabled) { background: #0f2a45; box-shadow: 0 4px 12px rgba(26,58,92,0.3); }
.uic-btn-success { background: #15803d; color: #fff; }
.uic-btn-success:hover:not(:disabled) { background: #166534; }
.uic-btn-warning { background: #d97706; color: #fff; }
.uic-btn-danger { background: #dc2626; color: #fff; }
.uic-btn-outline { background: transparent; color: #475569; border: 1px solid #d1d9e6; }
.uic-btn-outline:hover { background: #f1f5f9; }
.uic-btn-sm { padding: 7px 14px; font-size: 12px; min-height: 34px; }
.uic-btn-lg { padding: 12px 28px; font-size: 14px; min-height: 48px; }
.uic-btn-block { width: 100%; justify-content: center; }
.uic-btn-gold { background: #d4a843; color: #1a3a5c; }

/* ── Back Button ── */
.uic-back {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  background: transparent;
  border: 1px solid #d1d9e6;
  border-radius: 6px;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 20px;
  font-family: inherit;
}
.uic-back:hover { background: #f1f5f9; }

/* ── Alert ── */
.uic-alert {
  padding: 10px 14px;
  border-radius: 6px;
  font-size: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
}
.uic-alert-info { background: #eff6ff; border: 1px solid #bfdbfe; color: #1e40af; }
.uic-alert-warning { background: #fef3c7; border: 1px solid #fde68a; color: #b45309; }
.uic-alert-success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }

/* ── bordered box ── */
.uic-box {
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 16px;
  background: #fafbfc;
  margin-bottom: 12px;
}

/* ── Toast ── */
.uic-toast {
  position: fixed;
  bottom: 30px;
  left: 50%;
  transform: translateX(-50%);
  padding: 12px 20px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 500;
  z-index: 9999;
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 4px 20px rgba(0,0,0,0.2);
  max-width: 90vw;
  animation: uicFadeIn 0.3s ease;
}
.uic-toast.success { background: #f0fdf4; border: 1px solid #bbf7d0; color: #15803d; }
.uic-toast.danger { background: #fef2f2; border: 1px solid #fecaca; color: #dc2626; }
.uic-toast.warning { background: #fef3c7; border: 1px solid #fde68a; color: #b45309; }
@keyframes uicFadeIn { from { opacity: 0; transform: translateX(-50%) translateY(10px); } to { opacity: 1; transform: translateX(-50%) translateY(0); } }

/* ── Print ── */
@media print {
  @page { margin: 20mm; size: A4; }
  body { background: white !important; }
  .uic-paper { box-shadow: none !important; padding: 0 !important; max-width: none !important; border-radius: 0 !important; }
  .uic-actions, .uic-btn, .uic-back, .no-print { display: none !important; }
  .uic-signature-slot { border: 1px solid #ccc !important; break-inside: avoid; }
}

/* ── Responsive ── */
@media (max-width: 768px) {
  .uic-paper { padding: 24px 20px; }
  .uic-row { grid-template-columns: 1fr; gap: 10px; }
  .uic-signatures { grid-template-columns: 1fr 1fr; }
  .uic-actions { flex-direction: column; }
  .uic-btn { width: 100%; justify-content: center; }
}
@media (max-width: 480px) {
  .uic-paper { padding: 16px 14px; }
  .uic-header-republic { font-size: 15px; }
  .uic-header-title { font-size: 17px; }
  .uic-signatures { grid-template-columns: 1fr; }
}
  `}</style>
);

const StatusBadge = ({ status }) => {
  const colors = {
    draft: { bg: "rgba(245,158,11,0.1)", color: "#b45309" },
    pending: { bg: "rgba(245,158,11,0.1)", color: "#b45309" },
    signed: { bg: "rgba(22,163,74,0.1)", color: "#16a34a" },
    sent: { bg: "rgba(59,130,246,0.1)", color: "#2563eb" },
    sent_to_umudugudu: { bg: "rgba(99,102,241,0.1)", color: "#6366f1" },
    sent_to_kagari: { bg: "rgba(22,163,74,0.15)", color: "#16a34a" },
  };
  const s = colors[status] || { bg: "rgba(100,116,139,0.1)", color: "#64748b" };
  return (
    <span style={{
      display: "inline-flex", alignItems: "center", gap: "0.3rem", padding: "0.25rem 0.6rem",
      borderRadius: 20, fontSize: "0.72rem", fontWeight: 600, background: s.bg, color: s.color,
      border: `1px solid ${s.color}30`,
    }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: s.color }} />
      {status?.replace(/_/g, " ")}
    </span>
  );
};

function generateDocNumber() {
  const year = new Date().getFullYear();
  const rand = String(Math.floor(Math.random() * 999999) + 1).padStart(6, "0");
  return `IKR-${year}-${rand}`;
}

function formatDate() {
  return new Date().toLocaleDateString("rw-RW", { year: "numeric", month: "long", day: "numeric" });
}

export default function UmutekanoIcyiregoForm({ showMsg }) {
  const [records, setRecords] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [showDetail, setShowDetail] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ umwanditsi: "", ibisobanuro: "", itariki: "" });

  const [signMode, setSignMode] = useState(null);
  const [currentWitness, setCurrentWitness] = useState(0);
  const [witnesses, setWitnesses] = useState([]);
  const sigRef = useRef(null);
  const paperRef = useRef(null);
  const [witnessNames, setWitnessNames] = useState(["", "", "", ""]);
  const [docNumber] = useState(generateDocNumber);
  const [toast, setToast] = useState(null);

  const showToast = (text, variant) => {
    setToast({ text, variant });
    setTimeout(() => setToast(null), 4000);
  };

  const fetchData = useCallback(async () => {
    try { const r = await axios.get(`${API}/icyirego`); setRecords(r.data); } catch {}
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreateComplaint = async (e) => {
    e.preventDefault();
    try {
      if (editing) {
        await axios.put(`${API}/icyirego/${editing}`, form);
        showToast("Byavuguruwe!", "success");
      } else {
        const res = await axios.post(`${API}/icyirego`, form);
        showToast("Icyirego cyanditswe! Noneho mushobora gusinya.", "success");
        setShowDetail(res.data.id);
        setSignMode("author");
        setCurrentWitness(0);
        setWitnesses([]);
        setWitnessNames(["", "", "", ""]);
      }
      setShowForm(false);
      setEditing(null);
      setForm({ umwanditsi: "", ibisobanuro: "", itariki: "" });
      fetchData();
    } catch { showToast("Habaye ikosa", "danger"); }
  };

  const startSignature = (id) => {
    setShowDetail(id);
    setSignMode("author");
    setCurrentWitness(0);
    setWitnesses([]);
    setWitnessNames(["", "", "", ""]);
  };

  const handleAuthorSign = async () => {
    if (!sigRef.current || sigRef.current.isEmpty()) {
      showToast("Mbere sinya hano", "danger");
      return;
    }
    const sigData = sigRef.current.toDataURL();
    try {
      await axios.post(`${API}/icyirego/${showDetail}/sign`, { signature_image: sigData });
      showToast("Wasinye neza! Noneho andika umugabo wa 1.", "success");
      setSignMode("witness");
      setCurrentWitness(0);
      sigRef.current.clear();
    } catch { showToast("Habaye ikosa mu gusinya", "danger"); }
  };

  const handleWitnessSign = async () => {
    if (!witnessNames[currentWitness]?.trim()) {
      showToast(`Andika izina ry'umugabo wa ${currentWitness + 1}`, "danger");
      return;
    }
    if (!sigRef.current || sigRef.current.isEmpty()) {
      showToast(`Umugabo wa ${currentWitness + 1} asinye hano`, "danger");
      return;
    }
    const sigData = sigRef.current.toDataURL();
    try {
      await axios.post(`${API}/icyirego/${showDetail}/witness`, {
        name: witnessNames[currentWitness],
        signature_image: sigData,
        order_index: currentWitness + 1,
      });
      setWitnesses(prev => [...prev, { name: witnessNames[currentWitness], sig: sigData }]);
      sigRef.current.clear();
      if (currentWitness < 3) {
        setCurrentWitness(prev => prev + 1);
        showToast(`Umugabo wa ${currentWitness + 1} yasinye! Noneho andika umugabo wa ${currentWitness + 2}.`, "success");
      }
      if (currentWitness >= 3) {
        showToast("Bose basinye! Noneho ohereza ku Mudugudu.", "success");
        setSignMode("done");
      }
    } catch { showToast("Habaye ikosa", "danger"); }
  };

  const handleSendToMudugudu = async () => {
    try {
      await axios.post(`${API}/icyirego/${showDetail}/send`, {});
      showToast("Icyirego cyoherejwe ku Mudugudu!", "success");
      setShowDetail(null);
      setSignMode(null);
      setCurrentWitness(0);
      setWitnesses([]);
      setWitnessNames(["", "", "", ""]);
      fetchData();
    } catch { showToast("Habaye ikosa", "danger"); }
  };

  const viewDetail = async (id) => {
    try {
      const r = await axios.get(`${API}/icyirego/${id}`);
      setShowDetail(r.data);
      setWitnesses(r.data.witnesses || []);
    } catch { showToast("Habaye ikosa", "danger"); }
  };

  const handlePrintPDF = () => {
    window.print();
  };

  const handleNewForm = () => {
    setEditing(null);
    setForm({ umwanditsi: "", ibisobanuro: "", itariki: "" });
    setShowForm(true);
    setShowDetail(null);
    setSignMode(null);
  };

  const handleBack = () => {
    if (showDetail) {
      if (typeof showDetail === "object") {
        setShowDetail(null);
        fetchData();
      } else {
        if (signMode === "author" || signMode === "witness" || signMode === "done") {
          setSignMode(null);
          setShowDetail(null);
          setCurrentWitness(0);
          setWitnesses([]);
          setWitnessNames(["", "", "", ""]);
          fetchData();
        } else {
          setShowDetail(null);
          fetchData();
        }
      }
    } else if (showForm) {
      setShowForm(false);
    }
  };

  /* ── Document Content ── */
  const renderDocumentContent = (irego, isPreview = false) => {
    const doc = irego || form;
    return (
      <>
        <div className="uic-header">
          <div className="uic-header-republic">Repubulika y'u Rwanda</div>
          <div className="uic-header-title">Inyandiko y'ikirego</div>
          <div className="uic-header-docnum">{docNumber}</div>
          <div className="uic-header-motto">"Ubumwe, Umurimo, Gukunda Igihugu"</div>
        </div>

        <div className="uic-section">
          <div className="uic-section-title">
            <i className="bi bi-person-fill" /> Umwanditsi w'Ikirego
          </div>
          <div className="uic-section-body">
            <div className="uic-row uic-row-full">
              <div className="uic-field">
                <label>Amazina y'Umwanditsi</label>
                {isPreview ? (
                  <div style={{ padding: "10px 0", fontSize: 14, fontWeight: 500 }}>{doc.umwanditsi || "---"}</div>
                ) : (
                  <input value={doc.umwanditsi} onChange={(e) => setForm({ ...form, umwanditsi: e.target.value })} placeholder="Amazina y'umwanditsi" />
                )}
              </div>
              <div className="uic-field">
                <label>Itariki</label>
                {isPreview ? (
                  <div style={{ padding: "10px 0", fontSize: 14, fontWeight: 500 }}>
                    {doc.itariki ? (typeof doc.itariki === "string" ? doc.itariki.split("T")[0] : doc.itariki) : formatDate()}
                  </div>
                ) : (
                  <input type="date" value={doc.itariki} onChange={(e) => setForm({ ...form, itariki: e.target.value })} />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="uic-section">
          <div className="uic-section-title">
            <i className="bi bi-file-text-fill" /> Ibisobanuro by'Ikirego
          </div>
          <div className="uic-section-body">
            <div className="uic-field">
              {isPreview ? (
                <div style={{
                  minHeight: 200,
                  padding: "14px 16px",
                  background: "#fafbfc",
                  border: "1px solid #e2e8f0",
                  borderRadius: 5,
                  fontSize: 14,
                  lineHeight: 1.8,
                  whiteSpace: "pre-wrap",
                  color: doc.ibisobanuro ? "#1e293b" : "#94a3b8"
                }}>
                  {doc.ibisobanuro || "--- Nta bisobanuro byatanzwe ---"}
                </div>
              ) : (
                <textarea
                  className="uic-description-area"
                  value={doc.ibisobanuro}
                  onChange={(e) => setForm({ ...form, ibisobanuro: e.target.value })}
                  placeholder="Sobanura mu buryo burambuye ikirego..."
                  required
                />
              )}
            </div>
          </div>
        </div>

        {/* Signature Section */}
        <div className="uic-section">
          <div className="uic-section-title">
            <i className="bi bi-pencil-square" /> Abasinye
          </div>
          <div className="uic-section-body">
            <div className="uic-signatures">
              {/* Person 1: Complainant (Umutekano leader) */}
              <div className="uic-signature-slot">
                <div className="uic-sig-label">1. Umuyobozi Ushinzwe Umutekano</div>
                <div className="uic-sig-name">{isPreview ? (doc.umutekano_signed_by || "---") : "---"}</div>
                {isPreview && doc.umutekano_signature ? (
                  <img src={doc.umutekano_signature} alt="Umukono" className="uic-sig-image" />
                ) : (
                  <div className="uic-sig-placeholder">---</div>
                )}
                <div>
                  {(isPreview && doc.umutekano_signed_by) ? (
                    <span className="uic-sig-status signed"><i className="bi bi-check-circle-fill" /> Yasinye</span>
                  ) : (
                    <span className="uic-sig-status pending"><i className="bi bi-clock" /> Ntasinye</span>
                  )}
                </div>
              </div>

              {/* Witnesses */}
              {[0, 1, 2, 3].map((i) => {
                const w = isPreview ? (doc.witnesses || [])[i] : witnesses[i];
                return (
                  <div key={`w-${i}`} className="uic-signature-slot">
                    <div className="uic-sig-label">{i + 2}. Umugabo wa {i + 1}</div>
                    <div className="uic-sig-name">
                      {isPreview ? (w?.name || "---") : (witnesses[i]?.name || "---")}
                    </div>
                    {isPreview && w?.signature_image ? (
                      <img src={w.signature_image} alt="Umukono" className="uic-sig-image" />
                    ) : (!isPreview && witnesses[i]?.sig) ? (
                      <img src={witnesses[i].sig} alt="Umukono" className="uic-sig-image" />
                    ) : (
                      <div className="uic-sig-placeholder">---</div>
                    )}
                    <div>
                      {(isPreview && w?.signature_image) || (!isPreview && witnesses[i]?.sig) ? (
                        <span className="uic-sig-status signed"><i className="bi bi-check-circle-fill" /> Yasinye</span>
                      ) : (
                        <span className="uic-sig-status pending"><i className="bi bi-clock" /> Ntasinye</span>
                      )}
                    </div>
                  </div>
                );
              })}

              {/* Person 6: Umudugudu */}
              <div className="uic-signature-slot" style={{ borderColor: "#d4a843", background: "#fffcf0" }}>
                <div className="uic-sig-label" style={{ color: "#b8860b" }}>6. Umukuru w'Umudugudu</div>
                <div className="uic-sig-name">{isPreview ? (doc.mudugudu_signed_by || "---") : "---"}</div>
                {isPreview && doc.mudugudu_signature ? (
                  <img src={doc.mudugudu_signature} alt="Umukono" className="uic-sig-image" />
                ) : (
                  <div className="uic-sig-placeholder">---</div>
                )}
                <div>
                  {(isPreview && doc.mudugudu_signed_by) ? (
                    <span className="uic-sig-status signed"><i className="bi bi-check-circle-fill" /> Yasinye</span>
                  ) : (
                    <span className="uic-sig-status pending"><i className="bi bi-clock" /> Ntasinye</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  };

  /* ── Detail View ── */
  if (showDetail && typeof showDetail === "object") {
    const irego = showDetail;
    return (
      <>
        <Styles />
        <div style={{ padding: "20px 0" }}>
          <button className="uic-back" onClick={handleBack}>
            <i className="bi bi-arrow-left" /> Subira
          </button>
          <div className="uic-paper" ref={paperRef}>
            {renderDocumentContent(irego, true)}
            <div className="uic-actions">
              <button className="uic-btn uic-btn-outline" onClick={handlePrintPDF}>
                <i className="bi bi-printer" /> Print
              </button>
              {irego.status === "draft" && (
                <button className="uic-btn uic-btn-warning" onClick={() => startSignature(irego.id)}>
                  <i className="bi bi-pen" /> Tangira Gusinya
                </button>
              )}
            </div>
          </div>
        </div>
        {toast && <div className={`uic-toast ${toast.variant}`}><i className={`bi bi-${toast.variant === 'success' ? 'check-circle' : 'exclamation-circle'}`} /> {toast.text}</div>}
      </>
    );
  }

  /* ── Signature Form View (when showDetail is a number/ID) ── */
  if (showDetail && typeof showDetail === "number") {
    return (
      <>
        <Styles />
        <div style={{ padding: "20px 0" }}>
          <button className="uic-back" onClick={handleBack}>
            <i className="bi bi-arrow-left" /> Subira
          </button>
          <div className="uic-paper" ref={paperRef}>
            {/* Render document preview with whatever data we have */}
            <div className="uic-header">
              <div className="uic-header-republic">Repubulika y'u Rwanda</div>
              <div className="uic-header-title">Inyandiko y'ikirego</div>
              <div className="uic-header-docnum">{docNumber}</div>
              <div className="uic-header-motto">"Ubumwe, Umurimo, Gukunda Igihugu"</div>
            </div>

            {/* Signature Section */}
            <div className="uic-section">
              <div className="uic-section-title">
                <i className="bi bi-pencil-square" /> {signMode === "author" ? "Umukono w'Umuyobozi Ushinzwe Umutekano" : signMode === "witness" ? "Umukono w'Umuhamya" : "Gusinya birangiye"}
              </div>
              <div className="uic-section-body">
                {signMode === "author" && (
                  <div className="uic-box">
                    <div className="uic-alert uic-alert-info">
                      <i className="bi bi-info-circle" /> Nyamukono hano hanyuma ukande "Sinyisha"
                    </div>
                    <div style={{ fontWeight: 600, marginBottom: 8, fontSize: 14 }}>
                      <i className="bi bi-person-fill" /> {localStorage.getItem("fullname") || "Umuyobozi"}
                    </div>
                    <div className="uic-sig-pad">
                      <SignatureCanvas ref={sigRef} penColor="#1a3a5c" style={{ width: "100%" }}
                        canvasProps={{ width: 500, height: 150, className: "sig-canvas" }} />
                      <div className="uic-sig-actions">
                        <button className="uic-btn uic-btn-sm uic-btn-primary" onClick={handleAuthorSign}>
                          <i className="bi bi-pen" /> Sinyisha
                        </button>
                        <button className="uic-btn uic-btn-sm uic-btn-outline" onClick={() => sigRef.current?.clear()}>
                          <i className="bi bi-eraser" /> Kura
                        </button>
                      </div>
                    </div>
                  </div>
                )}

                {signMode === "witness" && currentWitness < 4 && (
                  <div className="uic-box">
                    <div className="uic-alert uic-alert-info">
                      <i className="bi bi-info-circle" /> Andika izina ry'umugabo wa {currentWitness + 1} hanyuma asinye
                    </div>
                    <div style={{ marginBottom: 10, fontWeight: 600, color: "#b45309", fontSize: 14 }}>
                      <i className="bi bi-person-plus-fill" /> Umugabo wa {currentWitness + 1}
                    </div>
                    <div style={{ marginBottom: 12 }}>
                      <input
                        style={{
                          width: "100%", padding: "10px 12px", border: "1px solid #d1d9e6",
                          borderRadius: 5, fontSize: 14, fontFamily: "inherit"
                        }}
                        placeholder={`Izina ry'umugabo wa ${currentWitness + 1}`}
                        value={witnessNames[currentWitness]}
                        onChange={(e) => {
                          const n = [...witnessNames];
                          n[currentWitness] = e.target.value;
                          setWitnessNames(n);
                        }}
                      />
                    </div>
                    <div className="uic-sig-pad">
                      <SignatureCanvas ref={sigRef} penColor="#1a3a5c" style={{ width: "100%" }}
                        canvasProps={{ width: 500, height: 150, className: "sig-canvas" }} />
                      <div className="uic-sig-actions">
                        <button className="uic-btn uic-btn-sm uic-btn-primary" onClick={handleWitnessSign}>
                          <i className="bi bi-pen" /> Sinyisha {witnessNames[currentWitness] || `Umugabo wa ${currentWitness + 1}`}
                        </button>
                        <button className="uic-btn uic-btn-sm uic-btn-outline" onClick={() => sigRef.current?.clear()}>
                          <i className="bi bi-eraser" /> Kura
                        </button>
                      </div>
                    </div>

                    {/* Signed witnesses list */}
                    {witnesses.length > 0 && (
                      <div style={{ marginTop: 14 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: "#15803d", marginBottom: 8 }}>
                          <i className="bi bi-check-circle-fill" /> Abasinye: {witnesses.length}/4
                        </div>
                        {witnesses.map((w, i) => (
                          <div key={i} style={{
                            display: "flex", alignItems: "center", gap: 8, padding: "6px 10px",
                            background: "#f0fdf4", border: "1px solid #bbf7d0", borderRadius: 5,
                            marginBottom: 4, fontSize: 13, color: "#065f46"
                          }}>
                            <i className="bi bi-check-circle-fill" style={{ color: "#16a34a" }} />
                            <span style={{ fontWeight: 600 }}>{w.name}</span>
                            {w.sig && <img src={w.sig} alt="sig" style={{ maxHeight: 24, borderRadius: 2 }} />}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {signMode === "done" && (
                  <div style={{ textAlign: "center", padding: "30px 20px" }}>
                    <i className="bi bi-check-circle-fill" style={{ fontSize: "3rem", color: "#16a34a" }} />
                    <h4 style={{ color: "#15803d", fontWeight: 700, margin: "12px 0 8px" }}>Bose basinye!</h4>
                    <p style={{ color: "#64748b", fontSize: 14, marginBottom: 20 }}>
                      Icyirego gisinywe na buri wese. Noneho ohereza ku Mudugudu.
                    </p>
                    <div className="uic-signatures" style={{ maxWidth: 500, margin: "0 auto 20px" }}>
                      <div className="uic-signature-slot">
                        <div className="uic-sig-label">Umuyobozi</div>
                        <span className="uic-sig-status signed"><i className="bi bi-check-circle-fill" /> Yasinye</span>
                      </div>
                      <div className="uic-signature-slot">
                        <div className="uic-sig-label">Abatangabuhamya</div>
                        <span className="uic-sig-status signed"><i className="bi bi-check-circle-fill" /> {witnesses.length} basinye</span>
                      </div>
                    </div>
                    <button className="uic-btn uic-btn-success uic-btn-lg" onClick={handleSendToMudugudu}>
                      <i className="bi bi-send-fill" /> Ohereza ku Mudugudu
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
        {toast && <div className={`uic-toast ${toast.variant}`}><i className={`bi bi-${toast.variant === 'success' ? 'check-circle' : 'exclamation-circle'}`} /> {toast.text}</div>}
      </>
    );
  }

  /* ── List + Create Form View ── */
  return (
    <>
      <Styles />
      <div>

        {/* Header + Add Button */}
        <div style={{
          display: "flex", justifyContent: "space-between", alignItems: "center",
          marginBottom: "1.25rem"
        }}>
          <h3 style={{ color: "#fff", fontWeight: 700, margin: 0, display: "flex", alignItems: "center", gap: "0.5rem" }}>
            <i className="bi bi-file-earmark-text" style={{ color: "#6c63ff" }} /> Kwandika Icyirego
          </h3>
          <button onClick={handleNewForm} className="uic-btn uic-btn-primary">
            <i className="bi bi-plus-lg" /> Ongeraho
          </button>
        </div>

        {/* Create Form */}
        {showForm && (
          <form onSubmit={handleCreateComplaint} style={{
            background: "#141b2d", border: "1px solid #1e2a45", borderRadius: 16,
            padding: "1.5rem", marginBottom: "1.5rem"
          }}>
            <div className="uic-paper" style={{ margin: 0, maxWidth: "100%" }}>
              {renderDocumentContent(form, false)}
              <div className="uic-actions">
                <button type="submit" className="uic-btn uic-btn-primary uic-btn-lg" style={{ flex: 1 }}>
                  <i className="bi bi-save" /> {editing ? "Vugurura" : "Emeza Kwandika Icyirego"}
                </button>
                <button type="button" className="uic-btn uic-btn-outline" onClick={() => setShowForm(false)}>
                  Hagarika
                </button>
              </div>
            </div>
          </form>
        )}

        {/* Data Table */}
        <div style={{
          background: "#141b2d", border: "1px solid #1e2a45", borderRadius: 16,
          padding: "1.25rem", overflow: "hidden"
        }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "1rem" }}>
            <input
              placeholder="Shakisha..."
              style={{
                width: "100%", maxWidth: 300, padding: "0.65rem 0.85rem", borderRadius: 10,
                border: "1px solid #1e2a45", background: "#0a0e1a", color: "#e8edf5",
                fontSize: "0.85rem", outline: "none"
              }}
            />
            <span style={{ color: "#8492a6", fontSize: "0.82rem" }}>{records.length} yose</span>
          </div>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ borderBottom: "1px solid #1e2a45" }}>
                  <th style={{ padding: "0.75rem 0.5rem", textAlign: "left", color: "#8492a6", fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase" }}>Umwanditsi</th>
                  <th style={{ padding: "0.75rem 0.5rem", textAlign: "left", color: "#8492a6", fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase" }}>Itariki</th>
                  <th style={{ padding: "0.75rem 0.5rem", textAlign: "left", color: "#8492a6", fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase" }}>Status</th>
                  <th style={{ padding: "0.75rem 0.5rem", textAlign: "left", color: "#8492a6", fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase" }}>Abasinye</th>
                  <th style={{ padding: "0.75rem 0.5rem", textAlign: "right", color: "#8492a6", fontSize: "0.78rem", fontWeight: 600, textTransform: "uppercase" }}>Ibikorwa</th>
                </tr>
              </thead>
              <tbody>
                {records.map((item, idx) => (
                  <tr key={item.id || idx} style={{ borderBottom: "1px solid #1e2a4533", transition: "background 0.2s" }}>
                    <td style={{ padding: "0.65rem 0.5rem", color: "#e8edf5", fontSize: "0.85rem" }}>{item.umwanditsi}</td>
                    <td style={{ padding: "0.65rem 0.5rem", color: "#e8edf5", fontSize: "0.85rem" }}>{item.itariki?.split("T")[0]}</td>
                    <td style={{ padding: "0.65rem 0.5rem", color: "#e8edf5", fontSize: "0.85rem" }}><StatusBadge status={item.status} /></td>
                    <td style={{ padding: "0.65rem 0.5rem", color: "#e8edf5", fontSize: "0.85rem" }}>
                      <span style={{ color: "#0095ff" }}>{item.witness_count || 0}</span>
                    </td>
                    <td style={{ padding: "0.65rem 0.5rem", textAlign: "right" }}>
                      <div style={{ display: "flex", gap: "0.35rem", justifyContent: "flex-end" }}>
                        <button
                          onClick={() => viewDetail(item.id)}
                          style={{
                            width: 32, height: 32, borderRadius: 8, border: "1px solid #1e2a45",
                            background: "#0a0e1a", color: "#8492a6", cursor: "pointer",
                            display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem"
                          }}
                          title="Reba"
                        >
                          <i className="bi bi-eye" />
                        </button>
                        {item.status === "draft" && (
                          <button
                            onClick={() => {
                              setForm({ umwanditsi: item.umwanditsi, ibisobanuro: item.ibisobanuro, itariki: item.itariki?.split("T")[0] || "" });
                              setEditing(item.id);
                              setShowForm(true);
                            }}
                            style={{
                              width: 32, height: 32, borderRadius: 8, border: "1px solid #1e2a45",
                              background: "#0a0e1a", color: "#ffaa00", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem"
                            }}
                            title="Hindura"
                          >
                            <i className="bi bi-pencil" />
                          </button>
                        )}
                        {item.status === "draft" && (
                          <button
                            onClick={() => startSignature(item.id)}
                            style={{
                              width: 32, height: 32, borderRadius: 8, border: "1px solid #1e2a45",
                              background: "#0a0e1a", color: "#0095ff", cursor: "pointer",
                              display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: "0.8rem"
                            }}
                            title="Sinyisha"
                          >
                            <i className="bi bi-pen" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {records.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ padding: "2rem", textAlign: "center", color: "#8492a6" }}>
                      Nta makuru aboneka
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
      {toast && <div className={`uic-toast ${toast.variant}`}><i className={`bi bi-${toast.variant === 'success' ? 'check-circle' : 'exclamation-circle'}`} /> {toast.text}</div>}
    </>
  );
}
